import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { Callout, Field } from '../../../shared/components/ui/primitives';
import { updatePreLeadDetails } from '../../services/api/implementation/impl';
import { emailLeadPortal } from '../../utils/emailLeadPrelead';

const inputClass =
  'w-full rounded-control border border-hairline-strong bg-surface px-3 py-2 text-sm text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600';

/**
 * Correct a pre-lead's name, phone or email.
 *
 * Mostly for leads read from a portal email, where the parser can pick up the
 * wrong text as a name. Only changed fields are sent, and the server writes
 * each change to the pre-lead's notes.
 */
export default function EditPreleadModal({ prelead, onClose }) {
  const queryClient = useQueryClient();
  const initial = {
    name: prelead?.name && prelead.name !== '—' ? prelead.name : '',
    countryCode: prelead?.countryCode || '+91',
    phone: prelead?.phone ?? '',
    email: prelead?.email && prelead.email !== '—' ? prelead.email : ''
  };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState('');
  const portal = emailLeadPortal(prelead);

  const changes = {};
  if (form.name.trim() !== initial.name.trim()) changes.name = form.name.trim();
  if (form.email.trim() !== initial.email.trim()) changes.email = form.email.trim() || null;
  if (form.phone.trim() !== String(initial.phone).trim()) changes.phone = form.phone.trim();
  if (form.countryCode.trim() !== initial.countryCode.trim()) {
    changes.countryCode = form.countryCode.trim();
  }
  const hasChanges = Object.keys(changes).length > 0;

  const mutation = useMutation({
    mutationFn: () => updatePreLeadDetails(prelead.pre_lead_id ?? prelead.id, changes),
    onSuccess: () => {
      toast.success('Pre-lead updated');
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId'] });
      queryClient.invalidateQueries({ queryKey: ['pendingTelecalling'] });
      queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
      queryClient.invalidateQueries({ queryKey: ['allPreLeads'] });
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.message || err?.message || 'Could not save')
  });

  const submit = (event) => {
    event.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Name is required.');
    if (form.phone.replace(/\D/g, '').length < 6) return setError('Enter a valid phone number.');
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      return setError('Enter a valid email address, or leave it empty.');
    }
    if (!hasChanges) return onClose();
    return mutation.mutate();
  };

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  return (
    <Modal name="Edit pre-lead" onClose={onClose} width={34} isDirty={hasChanges} confirmOnClose>
      <form onSubmit={submit} className="space-y-4">
        {portal ? (
          <Callout tone="brand">
            Read from a {portal === 'housing' ? 'Housing.com' : portal} email. Correct anything the
            reader got wrong — the original stays in the notes.
          </Callout>
        ) : null}

        <Field label="Name" htmlFor="edit-prelead-name" required>
          <input
            id="edit-prelead-name"
            className={inputClass}
            value={form.name}
            onChange={set('name')}
          />
        </Field>

        <div className="grid grid-cols-[6rem_1fr] gap-3">
          <Field label="Code" htmlFor="edit-prelead-cc">
            <input
              id="edit-prelead-cc"
              className={inputClass}
              value={form.countryCode}
              onChange={set('countryCode')}
            />
          </Field>
          <Field label="Phone" htmlFor="edit-prelead-phone" required>
            <input
              id="edit-prelead-phone"
              inputMode="tel"
              className={inputClass}
              value={form.phone}
              onChange={set('phone')}
            />
          </Field>
        </div>

        <Field label="Email" htmlFor="edit-prelead-email">
          <input
            id="edit-prelead-email"
            type="email"
            className={inputClass}
            value={form.email}
            onChange={set('email')}
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
